/**
 * Signature Register — one record of every document signed in the tenant.
 *
 * Spec: .claude/specs/document-templates-phase-2.md (Package J, AC15-AC21).
 *
 * Read-only on purpose. Everything here comes from stored signature events; a
 * register that recomputed a hash or a timestamp would be a report about the
 * present rather than a record of what happened.
 */
import React, { useEffect, useState } from "react";
import { useToast } from "../context/ToastContext";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { AsyncButton } from "../components/AsyncButton";
import { useIsMobile } from "../hooks/useIsMobile";
import { exportSignatureRegister, getSignatureRegister } from "../api";

const NAVY = "#1e3a8a";

const inputStyle: React.CSSProperties = {
  padding: "8px 11px", borderRadius: 6, border: "1px solid #e2e8f0",
  fontSize: 13, color: "#0f172a", boxSizing: "border-box",
};
const labelStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, color: "#334155", display: "block", marginBottom: 5,
};

const OUTCOME_TONE: Record<string, { bg: string; fg: string; ln: string }> = {
  Signed:   { bg: "#f0fdf4", fg: "#16a34a", ln: "#86efac" },
  Accepted: { bg: "#f0fdf4", fg: "#16a34a", ln: "#86efac" },
  Approved: { bg: "#f0fdf4", fg: "#16a34a", ln: "#86efac" },
  Declined: { bg: "#fef2f2", fg: "#dc2626", ln: "#fecaca" },
  Rejected: { bg: "#fef2f2", fg: "#dc2626", ln: "#fecaca" },
  Voided:   { bg: "#fffbeb", fg: "#b45309", ln: "#fde68a" },
};

export default function SignatureRegisterPage() {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [filters, setFilters] = useState({
    date_from: "", date_to: "", document_type: "", signer: "",
  });

  async function load() {
    setLoading(true);
    try {
      const active: any = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) active[k] = v; });
      setData(await getSignatureRegister(active));
    } catch (e: any) {
      toast(e?.response?.data?.detail || "Could not load the register", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  async function download() {
    try {
      const active: any = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) active[k] = v; });
      const blob = await exportSignatureRegister(active);
      const url = URL.createObjectURL(blob as Blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `signature-register-${data?.date_from}-to-${data?.date_to}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      toast("Could not export the register", "error");
    }
  }

  const rows: any[] = data?.rows || [];

  return (
    <div>
      <LoadingOverlay active={loading} />
      <div className="page-header">
        <div>
          <h1>Signature Register</h1>
          <p>Every document signed, declined or voided — letters and offers together</p>
        </div>
        <AsyncButton onClick={download}
          style={{ background: NAVY, color: "white", border: "none", padding: "9px 16px",
                   borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
          Export CSV
        </AsyncButton>
      </div>

      <div style={{
        background: "white", border: "1px solid #e2e8f0", borderRadius: 10,
        padding: 16, marginBottom: 18, display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12,
        alignItems: "end",
      }}>
        <div>
          <label style={labelStyle}>From</label>
          <input type="date" value={filters.date_from} style={{ ...inputStyle, width: "100%" }}
            onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} />
        </div>
        <div>
          <label style={labelStyle}>To</label>
          <input type="date" value={filters.date_to} style={{ ...inputStyle, width: "100%" }}
            onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} />
        </div>
        <div>
          <label style={labelStyle}>Document type</label>
          <input value={filters.document_type} placeholder="e.g. appointment"
            style={{ ...inputStyle, width: "100%" }}
            onChange={e => setFilters(f => ({ ...f, document_type: e.target.value }))} />
        </div>
        <div>
          <label style={labelStyle}>Person</label>
          <input value={filters.signer} placeholder="Name"
            style={{ ...inputStyle, width: "100%" }}
            onChange={e => setFilters(f => ({ ...f, signer: e.target.value }))} />
        </div>
        <button onClick={load}
          style={{ background: "white", color: "#334155", border: "1px solid #cbd5e1",
                   padding: "8px 15px", borderRadius: 6, fontWeight: 600, fontSize: 13,
                   cursor: "pointer" }}>Apply</button>
      </div>

      {data && (
        <div style={{ fontSize: 12.5, color: "#64748b", marginBottom: 12 }}>
          {data.total} record{data.total === 1 ? "" : "s"} from {data.date_from} to {data.date_to}
          {!data.offers_included && " · offers not included (Recruitment not enabled)"}
          {data.truncated && " · showing the most recent 2,000 — narrow the range to see more"}
        </div>
      )}

      {rows.length === 0 && !loading ? (
        <div style={{ textAlign: "center", padding: 50, color: "#64748b",
                      background: "#f8fafc", borderRadius: 10 }}>
          No signatures in this range.
        </div>
      ) : isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {rows.map((r, i) => {
            const tone = OUTCOME_TONE[r.outcome] || OUTCOME_TONE.Voided;
            return (
              <div key={i} style={{ background: "white", border: "1px solid #e2e8f0",
                                    borderRadius: 10, padding: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <strong style={{ fontSize: 14 }}>{r.signer_name || "—"}</strong>
                  <span style={{ background: tone.bg, color: tone.fg,
                                 border: `1px solid ${tone.ln}`, borderRadius: 999,
                                 padding: "2px 9px", fontSize: 11, fontWeight: 600 }}>
                    {r.outcome}
                  </span>
                </div>
                <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 4 }}>
                  {r.document_type} · {r.subject_name || "—"}
                </div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 4,
                              fontVariantNumeric: "tabular-nums" }}>
                  {r.occurred_at ? new Date(r.occurred_at).toLocaleString() : "—"}
                  {r.ip_address && <> · {r.ip_address}</>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ overflowX: "auto", background: "white",
                      border: "1px solid #e2e8f0", borderRadius: 10 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f1f5f9", textAlign: "left" }}>
                {["Document", "Subject", "Signed by", "Outcome", "When",
                  "IP", "Location", "Document hash"].map(h => (
                  <th key={h} style={{ padding: "10px 12px", fontSize: 12, fontWeight: 600,
                                       color: "#334155", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const tone = OUTCOME_TONE[r.outcome] || OUTCOME_TONE.Voided;
                return (
                  <tr key={i} style={{ borderTop: "1px solid #e2e8f0" }}>
                    <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                      {r.document_type}
                      {r.document_id && <span style={{ color: "#64748b" }}> #{r.document_id}</span>}
                    </td>
                    <td style={{ padding: "10px 12px" }}>{r.subject_name || "—"}</td>
                    <td style={{ padding: "10px 12px" }}>{r.signer_name || "—"}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ background: tone.bg, color: tone.fg,
                                     border: `1px solid ${tone.ln}`, borderRadius: 999,
                                     padding: "2px 9px", fontSize: 11, fontWeight: 600 }}>
                        {r.outcome}
                      </span>
                    </td>
                    <td style={{ padding: "10px 12px", whiteSpace: "nowrap",
                                 fontVariantNumeric: "tabular-nums" }}>
                      {r.occurred_at ? new Date(r.occurred_at).toLocaleString() : "—"}
                    </td>
                    <td style={{ padding: "10px 12px", fontVariantNumeric: "tabular-nums" }}>
                      {r.ip_address || "—"}
                    </td>
                    <td style={{ padding: "10px 12px" }}>{r.location || "—"}</td>
                    <td style={{ padding: "10px 12px", fontFamily: "monospace",
                                 fontSize: 11, color: "#64748b" }}
                        title={r.document_sha256 || ""}>
                      {r.document_sha256 ? `${r.document_sha256.slice(0, 12)}…` : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
